'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function BookPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferredDay: '',
    preferredTime: '',
    sessionType: 'in-person',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-br from-[#1B4D4A] to-[#2A6B67] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase mb-3" style={{color: '#99C5C2'}}>Take the First Step</p>
          <h1 className="font-serif text-5xl text-white mb-4" style={{fontFamily: 'Georgia, serif'}}>Book a Session</h1>
          <p className="text-white/80 text-base">
            <Link href="/" className="hover:text-white">Home</Link> <span className="mx-2">/</span> Book a Session
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-600 leading-relaxed text-base">
            I&apos;m glad you&apos;re here. Taking the first step towards counselling can feel daunting, but it is also a courageous act of self-care. Fill in the form below and I will be in touch within 24 hours to arrange your session.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="bg-[#F5F5F3] py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
              <svg className="w-16 h-16 text-[#1B4D4A] mx-auto mb-5" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              <h2 className="font-serif text-3xl text-gray-800 mb-4" style={{fontFamily: 'Georgia, serif'}}>Booking Request Received</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Thank you for reaching out. I will review your request and be in touch within 24 hours to confirm your appointment.
              </p>
              <p className="text-[#1B4D4A] text-xl italic" style={{fontFamily: '"Dancing Script", cursive'}}>
                &ldquo;You don&apos;t have to navigate life&apos;s storms alone.&rdquo; ♥
              </p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h2 className="font-serif text-3xl text-gray-800 mb-8" style={{fontFamily: 'Georgia, serif'}}>Your Booking Details</h2>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent"
                    placeholder="07700 000000"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Day</label>
                    <select
                      value={formData.preferredDay}
                      onChange={(e) => setFormData({...formData, preferredDay: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent bg-white"
                    >
                      <option value="">Select a day</option>
                      <option>Monday</option>
                      <option>Tuesday</option>
                      <option>Wednesday</option>
                      <option>Thursday</option>
                      <option>Friday</option>
                      <option>Saturday</option>
                      <option>Sunday</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Preferred Time</label>
                    <select
                      value={formData.preferredTime}
                      onChange={(e) => setFormData({...formData, preferredTime: e.target.value})}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent bg-white"
                    >
                      <option value="">Select a time</option>
                      <option>Morning (9am–12pm)</option>
                      <option>Afternoon (12pm–5pm)</option>
                      <option>Evening (5pm–8pm)</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Session Type *</label>
                  <div className="flex gap-4">
                    {['in-person', 'online'].map((type) => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="sessionType"
                          value={type}
                          checked={formData.sessionType === type}
                          onChange={(e) => setFormData({...formData, sessionType: e.target.value})}
                          className="accent-[#1B4D4A]"
                        />
                        <span className="text-sm text-gray-700 capitalize">{type === 'in-person' ? 'In-Person' : 'Online'}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Anything you&apos;d like to share? (optional)</label>
                  <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B4D4A] focus:border-transparent resize-none"
                    placeholder="A brief description of what has brought you to counselling (optional)..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-[#1B4D4A] text-white py-4 rounded-lg text-base font-medium hover:bg-[#2A6B67] transition-colors"
                >
                  Request My Session
                </button>
                <p className="text-gray-500 text-xs text-center">
                  By submitting this form you agree to our <Link href="/privacy" className="text-[#1B4D4A] hover:underline">Privacy Policy</Link>. All information is kept strictly confidential.
                </p>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* What to Expect */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl text-gray-800" style={{fontFamily: 'Georgia, serif'}}>What to Expect</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Submit Your Request', desc: 'Fill in the form above with your details and preferred appointment time.' },
              { step: '02', title: 'I\'ll Be in Touch', desc: 'I will contact you within 24 hours to confirm your appointment and answer any questions.' },
              { step: '03', title: 'Your First Session', desc: 'We will meet for an initial consultation to discuss your needs and how I can best support you.' },
            ].map((item, i) => (
              <div key={i} className="text-center">
                <div className="w-12 h-12 bg-[#E8F0EF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#1B4D4A] font-bold text-sm">{item.step}</span>
                </div>
                <h3 className="font-semibold text-gray-800 text-base mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-[#E8F0EF] rounded-xl p-6 text-center">
            <p className="text-[#1B4D4A] text-sm leading-relaxed">
              <strong>Please note:</strong> This form is for booking requests only. If you are in crisis or need immediate support, please contact the Samaritans on <strong>116 123</strong> or call 999.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
